const STYLE_MARKER = "data-bpp-search-styles"

export type ShadowHost = {
  host: HTMLElement
  shadow: ShadowRoot
  mount: HTMLElement
}

export function createShadowHost(container: HTMLElement): ShadowHost {
  const host = document.createElement("div")
  host.className = "bpp-search-host"
  host.style.display = "contents"
  container.appendChild(host)

  const shadow = host.attachShadow({ mode: "open" })
  relocateInjectedStyles(shadow)

  const mount = document.createElement("div")
  mount.className = "bpp-search-root"
  // Anchors the dropdown panel, which is absolutely positioned under the input.
  mount.style.position = "relative"
  shadow.appendChild(mount)

  return { host, shadow, mount }
}

export type ModalShadowHost = ShadowHost & {
  destroy: () => void
}

export const FADE_MS = 180

/** jsdom often has no `matchMedia`; treat that as motion allowed. */
export function prefersReducedMotion(): boolean {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false
}

/** Opacity starts at 0 so the first painted frame can transition to 1. */
function armFade(el: HTMLElement): void {
  if (prefersReducedMotion()) {
    return
  }
  el.style.opacity = "0"
  el.style.transition = `opacity ${FADE_MS}ms ease`
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (el.dataset.bppClosing === "1") {
        return
      }
      el.style.opacity = "1"
    })
  })
}

export function createModalShadowHost(zIndex: number): ModalShadowHost {
  const backdrop = document.createElement("div")
  backdrop.className = "bpp-search-backdrop"
  // Top edge matches a centered 700px slot (same as the app autocomplete). The card
  // hangs from that edge and grows downward; horizontal centering stays.
  backdrop.style.cssText = `position:fixed;inset:0;z-index:${zIndex};display:flex;align-items:flex-start;justify-content:center;padding:1rem;padding-top:max(1rem,calc((100dvh - min(700px,calc(100dvh - 2rem))) / 2));background:rgba(0,0,0,0.45);overscroll-behavior:none;`
  armFade(backdrop)
  document.body.appendChild(backdrop)

  const { shadow, mount } = attachModalShadow(backdrop)
  mount.style.cssText = "width:min(700px,100%);"

  return {
    host: backdrop,
    shadow,
    mount,
    destroy: () => backdrop.remove(),
  }
}

/**
 * Dropdown host for external triggers: a transparent full-screen layer (so
 * outside clicks still close) with the widget pinned under the trigger element.
 */
export function createAnchoredShadowHost(
  zIndex: number,
  anchor: HTMLElement
): ModalShadowHost {
  const backdrop = document.createElement("div")
  backdrop.className = "bpp-search-backdrop is-anchored"
  backdrop.style.cssText = `position:fixed;inset:0;z-index:${zIndex};background:transparent;`
  armFade(backdrop)
  document.body.appendChild(backdrop)

  const { shadow, mount } = attachModalShadow(backdrop)
  mount.style.position = "fixed"

  const position = () => {
    const rect = anchor.getBoundingClientRect()
    mount.style.top = `${rect.top}px`
    mount.style.left = `${rect.left}px`
    mount.style.width = `${rect.width}px`
  }
  position()

  // Reading the anchor's box forces a synchronous layout, and this runs on the
  // customer's scroll path. Coalesce to one measurement per frame.
  let frame = 0
  const schedule = () => {
    if (frame === 0) {
      frame = requestAnimationFrame(() => {
        frame = 0
        position()
      })
    }
  }

  window.addEventListener("resize", schedule)
  window.addEventListener("scroll", schedule, true)

  return {
    host: backdrop,
    shadow,
    mount,
    destroy: () => {
      cancelAnimationFrame(frame)
      window.removeEventListener("resize", schedule)
      window.removeEventListener("scroll", schedule, true)
      backdrop.remove()
    },
  }
}

function attachModalShadow(backdrop: HTMLDivElement): {
  shadow: ShadowRoot
  mount: HTMLDivElement
} {
  const shadow = backdrop.attachShadow({ mode: "open" })
  relocateInjectedStyles(shadow)

  const mount = document.createElement("div")
  mount.className = "bpp-search-modal-root"
  shadow.appendChild(mount)

  return { shadow, mount }
}

function relocateInjectedStyles(shadow: ShadowRoot): void {
  const styles = document.querySelectorAll<HTMLStyleElement>(
    `style[${STYLE_MARKER}], style[data-vite-dev-id*="search-embed"]`
  )
  styles.forEach((style) => {
    shadow.appendChild(style.cloneNode(true))
  })

  document.querySelectorAll("style").forEach((style) => {
    if (style.textContent?.includes("bpp-search-root")) {
      if (!shadow.querySelector(`style[data-bpp-cloned="${style.id || "inline"}"]`)) {
        const clone = style.cloneNode(true) as HTMLStyleElement
        clone.setAttribute("data-bpp-cloned", style.id || "inline")
        shadow.appendChild(clone)
      }
    }
  })
}

export function applyThemeVars(
  target: HTMLElement,
  theme?: { primary?: string; borderRadius?: string; fontFamily?: string }
): void {
  if (!theme) {
    return
  }
  if (theme.primary) {
    target.style.setProperty("--bpp-primary", theme.primary)
  }
  if (theme.borderRadius) {
    target.style.setProperty("--bpp-radius", theme.borderRadius)
  }
  if (theme.fontFamily) {
    target.style.setProperty("--bpp-font", theme.fontFamily)
  }
}
